import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { CommonModule } from '@angular/common';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { ApiService } from '../../../Services/api.service';
import { cloneDeep, isArray } from 'lodash';
import { ConfirmPopupComponent } from '../confirm-popup/confirm-popup.component';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDragPlaceholder,
  CdkDropList, moveItemInArray
} from '@angular/cdk/drag-drop';
import { DragDropModule } from '@angular/cdk/drag-drop';

@Component({
  selector: 'app-data-grid',
  standalone: true,
  imports: [NzTableModule, NzDividerModule, NzIconModule, CommonModule, NzSelectModule,
    FormsModule, NzDatePickerModule, ConfirmPopupComponent, NzSwitchModule, DragDropModule,
    CdkDropList, CdkDrag],
  templateUrl: './data-grid.component.html',
  styleUrl: './data-grid.component.scss'
})

export class DataGridComponent implements OnChanges {
  constructor(private apiService: ApiService) { }
  @Input() dataSet: any = [];
  @Input() columns: any = [];
  @Input() gridTitle: string = "";
  @Input() gridStyle: string = "";
  @Input() tableHeight: string = "";
  @Input() dropdownList: any[] = [];
  @Input() nestedParentColumns: any = [];
  @Input() nestedChildColumns: any = [];
  @Input() nestedParentDataSet: any = [];
  @Input() reOrderTable: boolean = false;
  @Input() nestedTable: boolean = false;
  @Input() frontPagination: boolean = true;
  @Input() searchTerm: string = '';
  @Output() deleteRow = new EventEmitter<any>();
  @Output() updateRow = new EventEmitter<any>();
  @Output() selectedRows = new EventEmitter<any>();
  @Output() toggledRows = new EventEmitter<any>();
  @Output() reOrderedRows = new EventEmitter<any>();
  @Output() editedRow = new EventEmitter<any>();
  @Output() tableScroll = new EventEmitter<Event>();

  pageSize = 50;
  pageIndex = 1;
  checked = false;
  indeterminate = false;
  setOfCheckedId = new Set<number>();
  editDataCopy: any[] = [];
  showModal: boolean = false;
  deleteData: any;
  listOfCurrentPageData: any[] = [];
  uniqeId: string = "";
  toggleData: any;
  reOrderDataSet: any = [];
  originalDataSet: any = [];
  @ViewChild('basicTable', { static: false, read: ElementRef }) set tableContent(content: ElementRef) {
    if (content) {
      this.attachScrollListener(content.nativeElement);
    }
  }

  attachScrollListener(element: HTMLElement) {
    const tableBody = element.querySelector('.ant-table-body');

    if (tableBody) {

      tableBody.removeEventListener('scroll', this.scrollHandler);
      tableBody.addEventListener('scroll', this.scrollHandler.bind(this));
    }
  }

  scrollHandler(event: Event) {
    if (this.reOrderTable || this.searchTerm) return;

    const target = event.target as HTMLElement;
    const threshold = 5;
    const isAtBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + threshold;

    if (isAtBottom) {
      this.tableScroll.emit(event);
    }
  }

  ngOnInit(): void {
    this.reOrderDataSet = this.originalDataSet = [...this.dataSet];
    this.setUniqueId();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['dataSet']) {
      this.originalDataSet = [...this.dataSet];
      if (!this.reOrderTable) {
        this.reOrderDataSet = [...this.dataSet];
      }
    }

    if (changes['reOrderTable'] && changes['reOrderTable'].currentValue === true) {
      this.reOrderDataSet = [...this.dataSet];
    }

    this.resetCheckBox();
  }

  setUniqueId() {
    this.columns
      .forEach((data: any) => { if (data.type == 'checkbox') { this.uniqeId = data.field } });
  }

  resetCheckBox() {
    this.checked = false;
    this.indeterminate = false;
    this.setOfCheckedId = new Set<number>();
  }

  onCurrentPageDataChange($event: any): void {
    this.listOfCurrentPageData = $event;
    this.refreshCheckedStatus(this.uniqeId);
  }

  onAllChecked(uniqueField: any, checked: boolean): void {
    this.listOfCurrentPageData
      .forEach((data: any) => { this.updateCheckedSet(data[uniqueField], checked) });
    this.refreshCheckedStatus(uniqueField);
  }

  updateCheckedSet(id: number, checked: boolean): void {
    if (checked) {
      this.setOfCheckedId.add(id);
    } else {
      this.setOfCheckedId.delete(id);
    }
  }

  refreshCheckedStatus(uniqueField: any): void {
    const listOfEnabledData = this.listOfCurrentPageData;
    this.checked = listOfEnabledData.every((data: any) => this.setOfCheckedId.has(data[uniqueField]));
    this.indeterminate = listOfEnabledData.some((data: any) => this.setOfCheckedId.has(data[uniqueField])) && !this.checked;
    this.selectedRows.emit(this.setOfCheckedId);
  }

  onItemChecked(uniqueField: any, id: number, checked: boolean): void {
    this.updateCheckedSet(id, checked);
    this.refreshCheckedStatus(uniqueField);
  }

  delete(data: any) {
    this.showModal = true;
    this.deleteData = data;
  }

  toggledData(data: any, evt: any) {
    const updatedData = { ...data };
    if ('dispCustomFields' in updatedData) {
      updatedData.dispCustomFields = evt === true ? 1 : 0;
    }
    if ('samePriceForAllSkus' in updatedData) {
      updatedData.samePriceForAllSkus = evt === true ? 1 : 0;
    }
    this.toggleData = updatedData;
    this.toggledRows.emit(this.toggleData);
  }

  onDeleteConfirm(): void {
    this.showModal = false;
    this.deleteRow.emit(this.deleteData);
    this.deleteData = null;
  }

  onDeleteCancel(): void {
    this.showModal = false;
    this.deleteData = null;
  }

  edit(data: any, index: number) {
    if (data.pageEdit) {
      this.editedRow.emit(data);
      return
    }
    this.editDataCopy[index] = cloneDeep(data);
    data.edit = !data.edit;
  }

  saveRowData(data: any, field: any) {
    data.edit = false;
    this.updateRow.emit(data);
  }

  updateFedexOrgCode(data: any) { }

  cancelSave(data: any, index: number) {
    let fieldsToRevert = ['tvalidFrom', 'tvalidTo', 'validFrom', 'validTo', 'garmentType', 'orgCode', 'locationCode', 'startDate', 'endDate', 'hideOnWeb', 'lotDesc'];
    fieldsToRevert.forEach((key: any) => {
      if (this.editDataCopy[index][key] !== undefined) {
        data[key] = this.editDataCopy[index][key];
      }
    });
    data.edit = false;
  }

  formatDate(data: any, col: any, evt: any) {
    if (col.allowClear && !evt) {
      return
    }
    if (col?.dateString) {
      data[col.field] = new Date(data[col.field]).toISOString();
    }
    else {
      data[col.field] = new Date(data[col.field]).toISOString().split('.')[0];
    }
  }

  drop(event: any) {
    moveItemInArray(this.reOrderDataSet, event.previousIndex, event.currentIndex);
    this.reOrderDataSet = [...this.reOrderDataSet];
    this.reOrderedRows.emit(this.reOrderDataSet);
  }

  searchData(searchTerm: string, searchField: string | string[], dataSet?: any) {
    this.searchTerm = searchTerm;
    if (!searchTerm) {
      this.dataSet = [...this.originalDataSet];
      this.searchTerm = '';
      return;
    }

    const cleanTerm = searchTerm.toLowerCase();
    const fields = Array.isArray(searchField) ? searchField : [searchField];
    if (dataSet) {
      this.dataSet = dataSet.filter((item: any) => {
        return fields.some((field) => {
          const value = item[field];
          return value != null &&
            value.toString().toLowerCase().includes(cleanTerm);
        });
      });
    }
    else {
      this.dataSet = this.originalDataSet.filter((item: any) => {
        return fields.some((field) => {
          const value = item[field];
          return value != null &&
            value.toString().toLowerCase().includes(cleanTerm);
        });
      });
    }
  }
}
